import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { InterviewerDetails, TimeSlot } from '../../types/booking';
import { BookingSummary, type BookingSummaryInterviewer } from '../booking/BookingSummary';
import { getFullImageUrl } from '../../utils/imageUtils';
import { DEFAULT_AVATAR } from '../../utils/defaultAvatar';

interface BookingModalProps {
  interviewer: InterviewerDetails;
  isOpen: boolean;
  onClose: () => void;
  onBookingComplete: () => void;
}

export function BookingModalNew({ interviewer, isOpen, onClose, onBookingComplete }: BookingModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isProfileComplete, setIsProfileComplete] = useState<boolean | null>(null);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [showSummary, setShowSummary] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setError(null);
      setSuccess(null);
      setShowSummary(false);
      setSelectedSlot(null);
      checkProfileCompletion();
      loadAvailableSlots();
    }
  }, [isOpen]);

  const checkProfileCompletion = async () => {
    try {
      const response = await apiService.profile.isProfileComplete();
      setIsProfileComplete(response.isComplete);
    } catch (error) {
      console.error('Failed to check profile completion:', error);
      setError(t('common.error'));
    }
  };

  const loadAvailableSlots = async () => {
    try {
      setLoading(true);
      const slots = await apiService.timeSlots.getAvailableTimeSlots(interviewer.id);
      setAvailableSlots(slots);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load time slots:', error);
      setError(t('common.error'));
      setLoading(false);
    }
  };

  const handleSlotSelect = (slot: TimeSlot) => {
    setSelectedSlot(slot);
  };

  const handleProceedToSummary = () => {
    if (!selectedSlot) {
      setError('Please select a time slot');
      return;
    }
    setShowSummary(true);
  };

  const handleBackToSlots = () => {
    setShowSummary(false);
  };

  const handleConfirmBooking = async (selectedCurrency?: 'USD' | 'GEL', selectedAmount?: number) => {
    if (!selectedSlot) return;

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      // Create payment intent with optional currency selection
      const paymentIntent = await apiService.bookings.createPaymentIntent(
        selectedSlot.id, 
        selectedCurrency, 
        selectedAmount
      );
      
      console.log('Payment intent created successfully:', {
        ...paymentIntent,
        selectedCurrency,
        selectedAmount,
        originalAmount: selectedSlot.amount,
        originalCurrency: selectedSlot.currency
      });
      
      setLoading(false);
      
      if (paymentIntent.success) {
        setSuccess(`Redirecting to payment (${paymentIntent.currency} ${paymentIntent.amount})...`);
        window.location.href = paymentIntent.paymentUrl;
        
        setTimeout(() => {
          onClose();
          onBookingComplete();
        }, 2000);
      } else {
        setError(paymentIntent.message || 'Payment setup failed');
      }
      
    } catch (error: any) {
      console.error('Payment intent creation failed:', error);
      setLoading(false);
      
      if (error.response?.data?.RequiresProfileCompletion) {
        setIsProfileComplete(false);
      } else {
        const errorMessage = error.response?.data?.Error || 
                           error.response?.data?.error ||
                           error.response?.data?.message ||
                           error.message ||
                           'Booking failed';
        setError(errorMessage);
      }
    }
  };

  const handleCompleteProfile = () => {
    onClose();
    navigate('/profile');
  };

  const formatDateTime = (dateTime: string) => {
    const date = new Date(dateTime);
    return {
      date: date.toLocaleDateString('en-GB', { 
        weekday: 'short',
        day: '2-digit',
        month: 'short', 
        year: 'numeric'
      }),
      time: date.toLocaleTimeString('en-GB', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      })
    };
  };

  const groupSlotsByDate = (slots: TimeSlot[]) => {
    const grouped: { [key: string]: TimeSlot[] } = {};
    slots.forEach(slot => {
      const date = new Date(slot.startTime).toDateString();
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(slot);
    });
    return grouped;
  };

  if (!isOpen) return null;

  // Show booking summary if slot is selected and user clicked proceed
  if (showSummary && selectedSlot) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 py-8">
          <div
          className="fixed inset-0 bg-stitch-overlay/50"
          onClick={handleBackToSlots}
          aria-hidden
        />
          <div className="relative z-50 w-full max-w-4xl">
            <BookingSummary
              interviewer={
                {
                  id: String(interviewer.id),
                  profileName: interviewer.name,
                  fullName: interviewer.name,
                  email: interviewer.email ?? '',
                  specialty: interviewer.specialty,
                  company: interviewer.company,
                  experience: interviewer.experience,
                  languages: interviewer.languages ?? [],
                  rating: interviewer.rating,
                  totalInterviews: interviewer.totalInterviews ?? 0,
                  profilePictureUrl: interviewer.profilePictureUrl ?? undefined,
                  linkedinUrl: interviewer.linkedinUrl,
                  country: interviewer.country,
                } satisfies BookingSummaryInterviewer
              }
              selectedSlot={selectedSlot}
              onConfirmBooking={handleConfirmBooking}
              onCancel={handleBackToSlots}
              loading={loading}
            />
          </div>
        </div>
      </div>
    );
  }

  const groupedSlots = groupSlotsByDate(availableSlots);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-4 sm:py-8">
        <div className="fixed inset-0 bg-stitch-overlay/50" onClick={onClose} aria-hidden />

        <div className="relative z-50 flex max-h-[95vh] w-full max-w-4xl flex-col overflow-hidden rounded-stitch-2xl border border-stitch-border bg-stitch-surface shadow-stitch-modal">
          {/* Header */}
          <div className="relative bg-gradient-to-r from-stitch-primary to-stitch-accent px-6 py-4 text-white">
            <button
              type="button"
              onClick={onClose}
              className="absolute right-4 top-4 text-white transition-colors hover:text-white/80"
            >
              <span className="material-symbols-outlined text-3xl">close</span>
            </button>
            <h2 className="text-2xl font-bold">Book Your Interview</h2>
            <p className="text-stitch-surface/90">Choose a time slot that works for you</p>
          </div>

          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            {/* Interviewer Info Card */}
            <div className="mb-4 rounded-stitch-2xl border border-stitch-border bg-gradient-to-br from-stitch-surface via-stitch-primary-soft/30 to-stitch-accent-soft/25 p-4 shadow-stitch-pop sm:p-6">
              <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6">
                {/* Profile Picture - Bigger */}
                <div className="flex-shrink-0">
                  {getFullImageUrl(interviewer.profilePictureUrl) ? (
                    <img 
                      src={getFullImageUrl(interviewer.profilePictureUrl) || DEFAULT_AVATAR} 
                      alt={interviewer.name}
                      className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl object-cover border-4 border-white shadow-2xl"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                        if (placeholder) placeholder.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className="flex h-32 w-32 items-center justify-center rounded-stitch-2xl border-4 border-stitch-surface bg-gradient-to-br from-stitch-primary via-stitch-accent to-stitch-accent-hover shadow-stitch-modal sm:h-40 sm:w-40"
                    style={{ display: getFullImageUrl(interviewer.profilePictureUrl) ? 'none' : 'flex' }}
                  >
                    <div className="text-center">
                      <span className="material-symbols-outlined mx-auto mb-2 block text-6xl text-white">person</span>
                      <div className="text-white text-sm font-bold">
                        {interviewer.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'IN'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Compact Info */}
                <div className="flex-1 min-w-0 text-center sm:text-left">
                  {/* Name & Email - Compact */}
                  <div className="mb-3">
                    <h3 className="text-lg sm:text-xl font-bold text-stitch-fg truncate">{interviewer.name}</h3>
                    {interviewer.email && (
                      <p className="text-sm text-stitch-muted truncate">{interviewer.email}</p>
                    )}
                  </div>
                  
                  {/* Professional Info - Inline */}
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 sm:gap-3 mb-3">
                    <span className="inline-flex items-center rounded-full bg-stitch-primary-soft px-3 py-1 text-sm font-semibold text-stitch-primary">
                      {interviewer.specialty}
                    </span>
                    {interviewer.company && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-stitch-surface-2 text-stitch-fg-soft">
                        @ {interviewer.company}
                      </span>
                    )}
                    {interviewer.experience && (
                      <span className="inline-flex items-center rounded-full bg-stitch-success-soft px-3 py-1 text-sm font-medium text-stitch-success dark:text-stitch-success">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                        {interviewer.experience}
                      </span>
                    )}
                  </div>

                  {/* Rating & Languages - Inline */}
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 sm:gap-4 mb-3">
                    <div className="flex items-center rounded-full border border-stitch-warning/30 bg-stitch-warning-soft/50 px-3 py-1 dark:bg-stitch-warning-soft/15">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <span
                          key={i}
                          className={`material-symbols-outlined text-lg ${
                            i < Math.floor(interviewer.rating) ? 'text-stitch-warning' : 'text-stitch-border'
                          }`}
                          style={{
                            fontVariationSettings:
                              i < Math.floor(interviewer.rating) ? "'FILL' 1, 'wght' 500" : "'wght' 400",
                          }}
                        >
                          star
                        </span>
                      ))}
                      <span className="ml-2 text-sm font-semibold text-stitch-fg-soft">
                        {interviewer.rating.toFixed(1)} ({interviewer.totalInterviews || 0})
                      </span>
                    </div>
                    
                    {interviewer.languages && interviewer.languages.length > 0 && (
                      <div className="flex items-center gap-1">
                        {interviewer.languages.filter(lang => lang && lang.trim()).slice(0, 3).map((lang, index) => (
                          <span 
                            key={index} 
                            className="inline-block rounded-full bg-stitch-accent-soft px-2 py-1 text-xs font-medium text-stitch-accent"
                          >
                            {lang.trim()}
                          </span>
                        ))}
                    </div>
                  )}
                </div>

                  {/* Location & LinkedIn - Inline */}
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 sm:gap-4">
                    {interviewer.country && (
                      <div className="flex items-center text-sm text-stitch-muted">
                        <span className="material-symbols-outlined mr-1 text-base text-stitch-muted">location_on</span>
                        {interviewer.country}
                      </div>
                    )}
                    
                    {interviewer.linkedinUrl && (
                      <a
                        href={interviewer.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center rounded-stitch-lg bg-stitch-primary px-3 py-1 text-sm font-medium text-white transition-colors hover:bg-stitch-primary-hover"
                      >
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd" />
                        </svg>
                        LinkedIn
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Profile Completion Check */}
            {isProfileComplete === false ? (
              <div className="rounded-stitch-lg border border-stitch-warning/30 bg-stitch-warning-soft/40 p-6 text-center dark:bg-stitch-warning-soft/10">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-stitch-warning-soft">
                  <span className="material-symbols-outlined text-2xl text-stitch-warning">person_alert</span>
                </div>
                <h3 className="mb-2 text-lg font-semibold text-stitch-fg">Complete Your Profile</h3>
                <p className="mb-4 text-stitch-fg-soft">
                  Please complete your profile before booking an interview. This helps us provide you with the best experience.
                </p>
                <button
                  onClick={handleCompleteProfile}
                  className="rounded-stitch-lg bg-stitch-warning px-6 py-2 text-white transition-colors hover:opacity-90"
                >
                  Complete Profile
                </button>
              </div>
            ) : (
              <>
                {/* Available Slots */}
                {loading ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-stitch-primary"></div>
                    <span className="ml-3 text-stitch-muted">Loading available slots...</span>
                  </div>
                ) : availableSlots.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-stitch-surface-2">
                      <span className="material-symbols-outlined text-5xl text-stitch-muted">event_busy</span>
                    </div>
                    <h3 className="text-xl font-semibold text-stitch-fg mb-2">No Available Slots</h3>
                    <p className="text-stitch-muted mb-4">This interviewer doesn't have any available time slots at the moment.</p>
                    <div className="mx-auto max-w-md rounded-stitch-lg border border-stitch-border bg-stitch-info-soft/40 p-4 dark:bg-stitch-info-soft/10">
                      <p className="text-sm text-stitch-fg">
                        <strong>What you can do:</strong>
                      </p>
                      <ul className="mt-2 space-y-1 text-sm text-stitch-fg-soft">
                        <li>• Check back later for new availability</li>
                        <li>• Contact the interviewer through LinkedIn</li>
                        <li>• Browse other interviewers with similar expertise</li>
                      </ul>
                    </div>
                    <div className="mt-6"></div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <h3 className="flex items-center text-lg font-semibold text-stitch-fg">
                      <span className="material-symbols-outlined mr-2 text-xl">calendar_month</span>
                      Available time slots
                    </h3>
                    
                    <div className="max-h-96 overflow-y-auto space-y-4">
                      {Object.entries(groupedSlots).map(([date, slots]) => (
                        <div key={date} className="rounded-stitch-xl border border-stitch-border bg-stitch-surface-2 p-4">
                          <h4 className="font-medium text-stitch-fg mb-3">
                            {new Date(date).toLocaleDateString('en-GB', { 
                              weekday: 'long',
                              day: '2-digit',
                              month: 'long', 
                              year: 'numeric'
                            })}
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                            {slots.map((slot) => {
                              const dateTime = formatDateTime(slot.startTime);
                              const endTime = new Date(slot.endTime).toLocaleTimeString('en-GB', { 
                                hour: '2-digit', 
                                minute: '2-digit',
                                hour12: true 
                              });
                              
                              return (
                                <button
                                  key={slot.id}
                                  onClick={() => handleSlotSelect(slot)}
                                  className={`rounded-stitch-lg border p-3 text-left transition-all ${
                                    selectedSlot?.id === slot.id
                                      ? 'bg-stitch-primary text-white shadow-stitch-pop ring-2 ring-stitch-ring'
                                      : 'border-stitch-border bg-stitch-surface hover:border-stitch-primary/40 hover:bg-stitch-primary-soft/30'
                                  }`}
                                >
                                  <div className="mb-1 flex items-center space-x-2">
                                    <span
                                      className={`material-symbols-outlined text-base ${
                                        selectedSlot?.id === slot.id ? 'text-stitch-surface/80' : 'text-stitch-muted'
                                      }`}
                                    >
                                      schedule
                                    </span>
                                    <span className="text-sm font-medium">
                                      {dateTime.time} - {endTime}
                                    </span>
                                  </div>
                                  {slot.amount && (
                                    <div className="flex items-center space-x-1">
                                      <span
                                        className={`material-symbols-outlined text-base ${
                                          selectedSlot?.id === slot.id ? 'text-stitch-success-soft' : 'text-stitch-success'
                                        }`}
                                      >
                                        payments
                                      </span>
                                      <span
                                        className={`text-sm font-bold ${selectedSlot?.id === slot.id ? 'text-stitch-success-soft' : 'text-stitch-success'}`}
                                      >
                                        ${slot.amount} {slot.currency || 'USD'}
                                      </span>
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {error && (
                  <div className="mt-4 rounded-stitch-lg border border-stitch-danger/30 bg-stitch-danger-soft/50 p-4 dark:bg-stitch-danger-soft/15">
                    <div className="flex">
                      <span className="material-symbols-outlined text-stitch-danger">error</span>
                      <div className="ml-3">
                        <p className="text-sm text-stitch-danger">{error}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Success Message */}
                {success && (
                  <div className="mt-4 rounded-stitch-lg border border-stitch-success/30 bg-stitch-success-soft/50 p-4 dark:bg-stitch-success-soft/15">
                    <div className="flex">
                      <span className="material-symbols-outlined text-stitch-success" style={{ fontVariationSettings: "'FILL' 1" }}>
                        check_circle
                      </span>
                      <div className="ml-3">
                        <p className="text-sm text-stitch-success">{success}</p>
                      </div>
                    </div>
                  </div>
                )}

              
              </>
            )}
          </div>
          
          {/* Sticky Footer with Action Buttons - Always show Cancel button */}
          <div className="border-t border-stitch-border p-4 sm:p-6 bg-stitch-surface">
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                  <button
                    onClick={onClose}
                className="w-full rounded-stitch-lg border border-stitch-border px-6 py-3 font-medium text-stitch-fg-soft transition-colors hover:bg-stitch-surface-2 focus:outline-none focus:ring-2 focus:ring-stitch-ring focus:ring-offset-2 focus:ring-offset-stitch-canvas sm:flex-1"
                  >
                    Cancel
                  </button>
              {/* Only show Continue button when there are slots and profile is complete */}
              {isProfileComplete !== false && availableSlots.length > 0 && (
                  <button
                    onClick={handleProceedToSummary}
                  disabled={!selectedSlot}
                  className={`w-full rounded-stitch-lg px-6 py-3 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 sm:flex-1 ${
                    selectedSlot
                        ? 'bg-stitch-primary text-white hover:bg-stitch-primary-hover focus:ring-stitch-ring'
                        : 'cursor-not-allowed bg-stitch-muted text-stitch-fg'
                    }`}
                  >
                    Continue to Summary
                  </button>
            )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

